/** NewAvatarModal — generate from prompt or upload a photo. */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { getAuthToken, queryClient } from "@/lib/queryClient";
import { sanitizeErrorMessage } from "@/lib/errors/gateway";
import { gptImage2Usd, usdToTokens } from "@shared/ugc-pricing";
import { Loader2, Upload, Wand2 } from "lucide-react";
import { AvatarUploadError, MAX_AVATAR_UPLOAD_BYTES, ugcStudioApi, type Gender } from "./api";

const MAX_UPLOAD_MB = Math.floor(MAX_AVATAR_UPLOAD_BYTES / (1024 * 1024));
// VP-125: real avatar token cost — single source of truth (@shared/ugc-pricing),
// shown in the button instead of the previously hard-coded "800".
const AVATAR_TOKEN_COST = usdToTokens(gptImage2Usd("medium"));

type Mode = "pick" | "generate" | "upload";
const DEFAULT_PROMPT = "Young UGC creator";
const DEFAULT_LANGUAGE = "Polish";
const DEFAULT_NATIONALITY = "Polish";
const LANGUAGE_OPTIONS = [
  "Polish",
  "English (American)",
  "English (British)",
  "German",
  "Spanish (Castilian)",
  "Spanish (Latin American)",
  "French",
  "Italian",
  "Portuguese (Brazilian)",
  "Czech",
  "Slovak",
  "Ukrainian",
  "Russian",
] as const;
const NATIONALITY_OPTIONS = [
  "Polish",
  "American",
  "British",
  "German",
  "Spanish",
  "Mexican",
  "French",
  "Italian",
  "Brazilian",
  "Czech",
  "Slovak",
  "Ukrainian",
  "Russian",
] as const;

interface NewAvatarModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function NewAvatarModal({ open, onClose, onCreated }: NewAvatarModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [mode, setMode] = useState<Mode>("pick");
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [gender, setGender] = useState<Gender | undefined>(undefined);
  const [language, setLanguage] = useState<string>(DEFAULT_LANGUAGE);
  const [nationality, setNationality] = useState<string>(DEFAULT_NATIONALITY);
  const [file, setFile] = useState<File | null>(null);

  const reset = () => {
    setMode("pick");
    setName("");
    setPrompt(DEFAULT_PROMPT);
    setGender(undefined);
    setLanguage(DEFAULT_LANGUAGE);
    setNationality(DEFAULT_NATIONALITY);
    setFile(null);
  };

  const generateMut = useMutation({
    mutationFn: () =>
      ugcStudioApi.generateAvatar({
        name: name || "Avatar",
        prompt,
        gender,
        spokenLanguage: language,
        nationality,
      }),
    // VP-125: refresh the token counter after a charge (success) or a refund
    // (error) so the sidebar balance is never stale.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      onCreated();
      reset();
      onClose();
    },
    onError: (err: Error) => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({
        title: t("ugcStudio.avatar.generationError"),
        description: sanitizeErrorMessage(err),
        variant: "destructive",
      });
    },
  });

  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Brak pliku");
      if (file.size > MAX_AVATAR_UPLOAD_BYTES) {
        throw new Error(
          t("ugcStudio.avatar.fileTooLarge", {
            sizeMb: (file.size / (1024 * 1024)).toFixed(1),
            maxMb: MAX_UPLOAD_MB,
          })
        );
      }
      const { avatar } = await ugcStudioApi.uploadAvatar({
        name: name || file.name,
        gender,
        spokenLanguage: language,
        nationality,
        file,
        token: getAuthToken() ?? "",
      });
      return { avatar };
    },
    onSuccess: () => {
      onCreated();
      reset();
      onClose();
    },
    onError: (err: Error) => {
      const description =
        err instanceof AvatarUploadError && err.code === "rejected-by-proxy"
          ? t("ugcStudio.avatar.uploadRejected", {
              status: err.status ?? "?",
              maxMb: MAX_UPLOAD_MB,
            })
          : err.message;
      toast({ title: t("ugcStudio.avatar.uploadError"), description, variant: "destructive" });
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className="bg-[#0a0c10] border-white/[0.08] max-w-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">{t("ugcStudio.avatar.newTitle")}</h3>
        </div>

        {mode === "pick" && <ModePicker onSelect={setMode} />}
        {mode === "generate" && (
          <GenerateForm
            name={name}
            prompt={prompt}
            gender={gender}
            language={language}
            nationality={nationality}
            isPending={generateMut.isPending}
            onName={setName}
            onPrompt={setPrompt}
            onGender={setGender}
            onLanguage={setLanguage}
            onNationality={setNationality}
            onSubmit={() => generateMut.mutate()}
          />
        )}
        {mode === "upload" && (
          <UploadForm
            name={name}
            file={file}
            gender={gender}
            language={language}
            nationality={nationality}
            isPending={uploadMut.isPending}
            onName={setName}
            onFile={setFile}
            onGender={setGender}
            onLanguage={setLanguage}
            onNationality={setNationality}
            onSubmit={() => uploadMut.mutate()}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ModePicker({ onSelect }: { onSelect: (m: Mode) => void }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-4">
      <button
        onClick={() => onSelect("generate")}
        className="flex flex-col items-center justify-center gap-2 p-8 rounded-xl border border-white/[0.08] hover:border-white/25 transition"
      >
        <Wand2 className="w-8 h-8 text-white/70" />
        <div className="text-white font-semibold">{t("ugcStudio.avatar.generateMode")}</div>
        <div className="text-white/50 text-xs text-center">
          {t("ugcStudio.avatar.generateModeDesc")}
        </div>
      </button>
      <button
        onClick={() => onSelect("upload")}
        className="flex flex-col items-center justify-center gap-2 p-8 rounded-xl border border-white/[0.08] hover:border-white/25 transition"
      >
        <Upload className="w-8 h-8 text-white/70" />
        <div className="text-white font-semibold">{t("ugcStudio.avatar.uploadMode")}</div>
        <div className="text-white/50 text-xs text-center">
          {t("ugcStudio.avatar.uploadModeDesc")}
        </div>
      </button>
    </div>
  );
}

interface GenerateFormProps {
  name: string;
  prompt: string;
  gender: Gender | undefined;
  language: string;
  nationality: string;
  isPending: boolean;
  onName: (v: string) => void;
  onPrompt: (v: string) => void;
  onGender: (g: Gender | undefined) => void;
  onLanguage: (l: string) => void;
  onNationality: (n: string) => void;
  onSubmit: () => void;
}

function GenerateForm({
  name,
  prompt,
  gender,
  language,
  nationality,
  isPending,
  onName,
  onPrompt,
  onGender,
  onLanguage,
  onNationality,
  onSubmit,
}: GenerateFormProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div>
        <label className="text-white/60 text-xs mb-1 block">
          {t("ugcStudio.avatar.nameLabel")}
        </label>
        <Input
          value={name}
          onChange={(e) => onName(e.target.value)}
          placeholder="@ my-avatar"
          className="bg-white/[0.04] border-white/[0.08] text-white"
        />
      </div>
      <div>
        <label className="text-white/60 text-xs mb-1 block">
          {t("ugcStudio.avatar.descLabel")}
        </label>
        <Input
          value={prompt}
          onChange={(e) => onPrompt(e.target.value)}
          className="bg-white/[0.04] border-white/[0.08] text-white"
        />
      </div>
      <GenderPicker gender={gender} onChange={onGender} />
      <LanguagePicker language={language} onChange={onLanguage} />
      <NationalityPicker nationality={nationality} onChange={onNationality} />
      <Button
        onClick={onSubmit}
        disabled={isPending || !prompt}
        className="w-full bg-lime-400 hover:bg-lime-300 text-black"
      >
        {isPending ? (
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
        ) : (
          <Wand2 className="w-4 h-4 mr-2" />
        )}
        {t("ugcStudio.avatar.generateButton", { cost: AVATAR_TOKEN_COST })}
      </Button>
    </div>
  );
}

interface UploadFormProps {
  name: string;
  file: File | null;
  gender: Gender | undefined;
  language: string;
  nationality: string;
  isPending: boolean;
  onName: (v: string) => void;
  onFile: (f: File | null) => void;
  onGender: (g: Gender | undefined) => void;
  onLanguage: (l: string) => void;
  onNationality: (n: string) => void;
  onSubmit: () => void;
}

function UploadForm({
  name,
  file,
  gender,
  language,
  nationality,
  isPending,
  onName,
  onFile,
  onGender,
  onLanguage,
  onNationality,
  onSubmit,
}: UploadFormProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div>
        <label className="text-white/60 text-xs mb-1 block">
          {t("ugcStudio.avatar.nameLabel")}
        </label>
        <Input
          value={name}
          onChange={(e) => onName(e.target.value)}
          placeholder="@ my-avatar"
          className="bg-white/[0.04] border-white/[0.08] text-white"
        />
      </div>
      <GenderPicker gender={gender} onChange={onGender} />
      <LanguagePicker language={language} onChange={onLanguage} />
      <NationalityPicker nationality={nationality} onChange={onNationality} />
      <label className="flex flex-col items-center justify-center gap-2 p-8 rounded-xl border border-dashed border-white/[0.12] hover:border-white/25 cursor-pointer transition">
        <Upload className="w-6 h-6 text-white/60" />
        <span className="text-white/60 text-sm">
          {file ? file.name : t("ugcStudio.avatar.chooseFile")}
        </span>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
        />
      </label>
      <Button
        onClick={onSubmit}
        disabled={isPending || !file}
        className="w-full bg-lime-400 hover:bg-lime-300 text-black"
      >
        {isPending ? (
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
        ) : (
          <Upload className="w-4 h-4 mr-2" />
        )}
        {t("ugcStudio.avatar.addButton")}
      </Button>
    </div>
  );
}

function GenderPicker({
  gender,
  onChange,
}: {
  gender: Gender | undefined;
  onChange: (g: Gender | undefined) => void;
}) {
  const { t } = useTranslation();
  const chip = (g: Gender, label: string) => (
    <button
      onClick={() => onChange(gender === g ? undefined : g)}
      className={`px-3 py-1.5 rounded-md text-xs transition ${
        gender === g
          ? "bg-lime-400 text-black"
          : "bg-white/[0.04] text-white/60 hover:bg-white/[0.08]"
      }`}
    >
      {label}
    </button>
  );
  return (
    <div>
      <label className="text-white/60 text-xs mb-1 block">
        {t("ugcStudio.avatar.genderOptional")}
      </label>
      <div className="flex gap-2">
        {chip("male", "♂ Male")}
        {chip("female", "♀ Female")}
        {chip("neutral", "Neutral")}
      </div>
    </div>
  );
}

function LanguagePicker({
  language,
  onChange,
}: {
  language: string;
  onChange: (l: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <label className="text-white/60 text-xs mb-1 block">
        {t("ugcStudio.avatar.languageLabel")}
      </label>
      <select
        value={language}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:border-white/20"
      >
        {LANGUAGE_OPTIONS.map((opt) => (
          <option key={opt} value={opt} className="bg-[#0a0c10]">
            {opt}
          </option>
        ))}
      </select>
      <p className="text-white/40 text-[11px] mt-1">{t("ugcStudio.avatar.languageHint")}</p>
    </div>
  );
}

function NationalityPicker({
  nationality,
  onChange,
}: {
  nationality: string;
  onChange: (n: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <label className="text-white/60 text-xs mb-1 block">
        {t("ugcStudio.avatar.nationalityLabel")}
      </label>
      <select
        value={nationality}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:border-white/20"
      >
        {NATIONALITY_OPTIONS.map((opt) => (
          <option key={opt} value={opt} className="bg-[#0a0c10]">
            {opt}
          </option>
        ))}
      </select>
      <p className="text-white/40 text-[11px] mt-1">{t("ugcStudio.avatar.nationalityHint")}</p>
    </div>
  );
}
